import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarDays } from 'lucide-react'
import { Card } from '@/components/ui'
import { listPedagogyTeacherLessonHistory, type LessonHistorySortKey, type PedagogyTeacherLessonHistoryRow } from '@/services/pedagogyTeacherLessonHistoryService'

const PAGE_SIZE = 20

function formatDate(value: string) {
  return new Date(`${value}T00:00:00`).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })
}

function sortIcon(active: boolean, direction: 'asc' | 'desc') {
  if (!active) return <ArrowUpDown size={13} />
  return direction === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />
}

export function PartnerTeacherLessonHistory({ periodId, weekNumber, partnerNumber, cityId, teacherId }: { periodId: number; weekNumber: number | null; partnerNumber: number; cityId: number; teacherId: string | null }) {
  const [rows, setRows] = useState<PedagogyTeacherLessonHistoryRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [sort, setSort] = useState<{ key: LessonHistorySortKey; direction: 'asc' | 'desc' }>({ key: 'date', direction: 'desc' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setOffset(0)
  }, [periodId, weekNumber, partnerNumber, cityId, teacherId, sort])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    void listPedagogyTeacherLessonHistory({ periodId, weekNumber, partnerNumber, cityId, teacherId, limit: PAGE_SIZE, offset, sortBy: sort.key, sortDirection: sort.direction })
      .then(page => {
        if (cancelled) return
        setRows(current => offset === 0 ? page.rows : [...current, ...page.rows])
        setTotalCount(page.totalCount)
      })
      .catch(value => { if (!cancelled) setError(value instanceof Error ? value.message : 'Не удалось загрузить историю занятий') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [cityId, offset, partnerNumber, periodId, sort, teacherId, weekNumber])

  const changeSort = (key: LessonHistorySortKey) => setSort(current => current.key === key ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: key === 'date' ? 'desc' : 'asc' })
  const headers: Array<[LessonHistorySortKey, string]> = [['date', 'Дата и время'], ['address', 'Город и адрес'], ['direction', 'Направление'], ['group', 'Группа'], ['visitors', 'Посетили']]

  return <section className="space-y-3">
    <div className="flex items-center gap-2"><CalendarDays size={17} className="text-primary" /><h2 className="font-semibold">История занятий</h2></div>
    {error && <Card className="border-error/30 bg-error/5 text-sm text-error">{error}</Card>}
    <Card className="overflow-hidden !p-0">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-muted/30 text-left text-xs text-muted-foreground"><tr>{headers.map(([key, label]) => <th key={key} className="px-4 py-3 font-medium"><button type="button" className="inline-flex items-center gap-1.5" onClick={() => changeSort(key)}>{label}{sortIcon(sort.key === key, sort.direction)}</button></th>)}</tr></thead>
          <tbody className="divide-y divide-border">
            {rows.map((row, index) => <tr key={`${row.lessonDate}-${row.lessonTime}-${row.directionName}-${row.groupName}-${index}`}><td className="whitespace-nowrap px-4 py-3">{formatDate(row.lessonDate)}{row.lessonTime ? ` · ${row.lessonTime.slice(0, 5)}` : ''}</td><td className="px-4 py-3">{[row.cityName, row.studioAddress].filter(Boolean).join(' · ') || '—'}</td><td className="px-4 py-3">{row.directionName || '—'}</td><td className="px-4 py-3">{row.groupName || '—'}</td><td className="px-4 py-3 tabular-nums">{row.visitorsCount}</td></tr>)}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Нет занятий за выбранный период</td></tr>}
          </tbody>
        </table>
      </div>
      {loading && <div className="border-t border-border px-4 py-3 text-xs text-muted-foreground">Загружаем историю…</div>}
      {!loading && rows.length < totalCount && <div className="border-t border-border px-4 py-3 text-center"><button type="button" className="text-sm font-medium text-primary hover:underline" onClick={() => setOffset(current => current + PAGE_SIZE)}>Показать ещё</button></div>}
    </Card>
  </section>
}
